import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { EAIChatPageId } from 'types/ai';
import { EPPAStep, ISaveBasicInfo } from 'types/ppaOnboarding';
import usePPAOnboardingReviewInfo from 'hooks/usePPAOnboardingReviewInfo';
import { getStep, savePPAOnboardingBasicInfo, saveStep } from 'api/ppaOnboarding';
import CreateProfile3 from 'pages/PPAOnboardingSteps/components/Steps/CreateProfile/Step3';
import CreateProfile4 from 'pages/PPAOnboardingSteps/components/Steps/CreateProfile/Step4';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { EInvitedOnboardingStep } from '../types';
import { EInvitedProviderStep, INVITED_STEP_ROUTE, isInvitedProviderStep } from '../steps';
import s from './s.module.less';

type SubStep = EPPAStep.STEP_3 | EPPAStep.STEP_4;

const SUB_STEP_ORDER: SubStep[] = [EPPAStep.STEP_3, EPPAStep.STEP_4];

const FORM_IDS: Record<SubStep, string> = {
    [EPPAStep.STEP_3]: 'invited-credentials',
    [EPPAStep.STEP_4]: 'invited-gender',
};

const AI_CHAT_BY_SUB_STEP: Record<SubStep, EAIChatPageId> = {
    [EPPAStep.STEP_3]: EAIChatPageId.INVITED_PROVIDER_CREATE_PROFILE_CREDENTIALS,
    [EPPAStep.STEP_4]: EAIChatPageId.INVITED_PROVIDER_CREATE_PROFILE_GENDER,
};

const CreateProfilePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const cameFromNextStep = (location.state as { fromNext?: boolean } | null)?.fromNext === false;
    const [reviewData, , refetchReviewData] = usePPAOnboardingReviewInfo();
    const [subStep, setSubStep] = useState<SubStep>(cameFromNextStep ? EPPAStep.STEP_4 : EPPAStep.STEP_3);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [nextDisabled, setNextDisabled] = useState(false);
    const [selectedGender, setSelectedGender] = useState<string | undefined>(reviewData?.basicInfo?.gender);

    useEffect(() => {
        setNextDisabled(false);
    }, [subStep]);

    // Seed the gender selection from pre-filled data so the footer "Next" works without re-clicking.
    useEffect(() => {
        if (reviewData?.basicInfo?.gender) {
            setSelectedGender((prev) => prev ?? reviewData.basicInfo?.gender);
        }
    }, [reviewData?.basicInfo?.gender]);

    // Resume the invited-provider onboarding based on the persisted step.
    // If the user is past create-profile, send them forward; if they're on
    // create-profile's second sub-step (gender), restore that sub-step.
    useEffect(() => {
        if (cameFromNextStep) return undefined;
        let cancelled = false;
        (async () => {
            const res = await getStep();
            const saved = res?.data?.data?.currentStep;
            if (cancelled || !isInvitedProviderStep(saved)) {
                return;
            }
            if (saved === EInvitedProviderStep.CREATE_PROFILE_CREDENTIALS) {
                return;
            }
            if (saved === EInvitedProviderStep.CREATE_PROFILE_GENDER) {
                setSubStep(EPPAStep.STEP_4);
                return;
            }
            const target = INVITED_STEP_ROUTE[saved];
            if (target && target !== PATH.INVITED_PROVIDER_ONBOARDING_CREATE_PROFILE) {
                navigate(target, { replace: true });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const goToNextSubStep = useCallback(async () => {
        const idx = SUB_STEP_ORDER.indexOf(subStep);
        if (idx < SUB_STEP_ORDER.length - 1) {
            const nextSubStep = SUB_STEP_ORDER[idx + 1];
            await saveStep(EInvitedProviderStep.CREATE_PROFILE_GENDER);
            setSubStep(nextSubStep);
            refetchReviewData?.();
        } else {
            await saveStep(EInvitedProviderStep.SETUP_SERVICES_SPECIALTY);
            navigate(PATH.INVITED_PROVIDER_ONBOARDING_SERVICES);
        }
    }, [subStep, navigate, refetchReviewData]);

    const handleNext = useCallback(
        async (data: ISaveBasicInfo) => {
            try {
                setSubmitLoading(true);
                const res = await savePPAOnboardingBasicInfo(data);
                if (res && !res.error) {
                    goToNextSubStep();
                } else {
                    message.error(res?.error);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setSubmitLoading(false);
            }
        },
        [goToNextSubStep],
    );

    const handleBack = useCallback(() => {
        const idx = SUB_STEP_ORDER.indexOf(subStep);
        if (idx > 0) {
            setSubStep(SUB_STEP_ORDER[idx - 1]);
        } else {
            navigate(PATH.INVITED_PROVIDER_ONBOARDING_ACTIVATE);
        }
    }, [subStep, navigate]);

    const handleSkip = useCallback(async () => {
        await saveStep(EInvitedProviderStep.SETUP_SERVICES_SPECIALTY);
        navigate(PATH.INVITED_PROVIDER_ONBOARDING_SERVICES);
    }, [navigate]);

    const formId = FORM_IDS[subStep];

    const contentRender = useMemo(() => {
        if (subStep === EPPAStep.STEP_3) {
            return (
                <CreateProfile3
                    initValue={reviewData.basicInfo}
                    formId={formId}
                    onNext={handleNext}
                    setNextButtonDisabled={setNextDisabled}
                    wrapClassName={s.stepWrap}
                />
            );
        }
        return (
            <CreateProfile4
                gender={selectedGender}
                onChange={(v) => {
                    setSelectedGender(v);
                    handleNext({ gender: v });
                }}
                wrapClassName={s.stepWrap}
            />
        );
    }, [subStep, reviewData, formId, handleNext, selectedGender]);

    const isGenderStep = subStep === EPPAStep.STEP_4;
    const footerRender = (
        <Footer
            showBack={subStep !== EPPAStep.STEP_3}
            onBack={handleBack}
            showNext
            // Step 4 (gender) has no form — drive Next via the selected value instead of a form submit.
            nextFormId={isGenderStep ? undefined : formId}
            onNext={isGenderStep ? () => handleNext({ gender: selectedGender }) : undefined}
            nextLoading={submitLoading}
            nextDisabled={isGenderStep ? !selectedGender : nextDisabled}
        />
    );

    return (
        <Layout
            step={EInvitedOnboardingStep.CREATE_PROFILE}
            aiChatPageId={AI_CHAT_BY_SUB_STEP[subStep]}
            footer={footerRender}
        >
            {contentRender}
        </Layout>
    );
};

export default CreateProfilePage;
